import { useEffect, useMemo, useState } from 'react';
import { useParams, useNavigate } from 'react-router';

import { Visibility, OpenInNew, ViewList, ViewModule, Description, Terminal } from '@mui/icons-material';
import {
  Box,
  List,
  ListItem,
  ListItemContent,
  Typography,
  Card,
  // CardOverflow,
  // AspectRatio,
  // IconButton,
  Chip,
  Modal,
  ModalDialog,
  Divider,
  Sheet,
  Avatar,
  Button,
  ModalClose,
  Select,
  Option
} from '@mui/joy';
import Pagination, { usePagination, SearchInput } from './Pagination';
import useUser from './auth/useUser';
import { formatTimestamp, getCdnFileUrl, parseGraphQLError } from '../utils';
import { Stack } from '@mui/material';
import { socialMediaDictionary } from '../social';
import { RECORDING_STATES, RECORDING_STATES_LABELS, APPLICATION_STAGES, APPLICATION_STAGES_LABELS, APPLICATION_STAGES_COLORS, getRejectionMessageTemplate, REJECTION_TEMPLATE_FEEDBACK_PLACEHOLDER } from '../constants';
import useUpToDateRecordingState from './useUpToDateRecordingState';
import { useFeedback } from './feedback/Service';
import FormRenderer from './FormRenderer';
import KanbanBoard from './KanbanBoard';
import { gql, useMutation, useLazyQuery, useQuery } from '@apollo/client';
import { GET_APPLICATION } from '../gql/common/application.query';
import { GET_APPLICATIONS } from '../gql/common/applications.query';
import { ENQUEUE_EVALUATE_APPLICATION_MUTATION } from '../gql/admin/enqueueEvaluateApplication.mutation';
import { INTERVIEW_LOGS_QUERY } from '../gql/admin/interviewLogs.query';
import useTracking from './useTracking';
import { Video } from './VideoJs';
import { useOnboarding } from './onboarding/OnboardingTour';

const getScoreColor = (score) => {
  if (score >= 80) return 'success';
  if (score >= 60) return 'warning';
  return 'danger';
};

const getApplicationProgress = (application) => {
  // Based on the logic from Dashboard.jsx
  const hasQuestionnaire = application.questionnaire;
  const passedQuestionnaire = application.questionnaire && application.questionnaire.passed;
  const failedQuestionnaire = hasQuestionnaire && !passedQuestionnaire;
  const hasEvaluation = application.evaluation && Object.keys(application.evaluation).length > 0;

  if (hasEvaluation) {
    return { step: 4, label: 'Recorded video interview', color: 'success' };
  } else if (failedQuestionnaire) {
    return { step: 3, label: 'Failed screening questions', color: 'danger' };
  } else if (passedQuestionnaire) {
    return { step: 2, label: 'Passed screening questions', color: 'warning' };
  } else {
    return { step: 1, label: 'Created profile', color: 'neutral' };
  }
};

const EvaluationInfo = ({ application }) => {
  const recording = useUpToDateRecordingState(application.interview?.recording || {});
  console.log(application.evaluation);
  return (
    <>
      {recording.state === RECORDING_STATES.READY ?
        <Video recordingId={application.interview.recording._id} durations={application.interview.durations} /> :
        <Typography level="body-md">Recording state: {RECORDING_STATES_LABELS[recording.state || "Not found"]}</Typography>
      }

      <Sheet
        variant="outlined"
        sx={{ mt: 3, p: 2, borderRadius: 'sm' }}
      >
        <Typography level="title-md" mb={1}>
          Summary
        </Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
          <Typography level="body-md" mr={2}>Overall Score:</Typography>
          <Chip
            size="md"
            color={getScoreColor(application.finalScore || 0)}
          >
            {application.finalScore || 0}/100
          </Chip>
        </Box>

        <Typography level="body-md" mb={1}>
          {application.evaluation.finalEvaluation?.summary || 'No summary available'}
        </Typography>

        {application.evaluation.finalEvaluation?.strengths?.length > 0 && (
          <>
            <Typography level="title-sm" mt={2}>Strengths:</Typography>
            <List marker="disc" sx={{ pl: 2 }}>
              {application.evaluation.finalEvaluation.strengths.map((strength, idx) => (
                <ListItem key={idx}>
                  <ListItemContent>{strength}</ListItemContent>
                </ListItem>
              ))}
            </List>
          </>
        )}

        {application.evaluation.finalEvaluation?.weaknesses?.length > 0 && (
          <>
            <Typography level="title-sm" mt={2}>Areas for Improvement:</Typography>
            <List marker="disc" sx={{ pl: 2 }}>
              {application.evaluation.finalEvaluation.weaknesses.map((weakness, idx) => (
                <ListItem key={idx}>
                  <ListItemContent>{weakness}</ListItemContent>
                </ListItem>
              ))}
            </List>
          </>
        )}

        {application.evaluation.finalEvaluation?.hiring_signal && (
          <Typography level="body-md" my={1}>
            Hiring Signal: <strong>{application.evaluation.finalEvaluation?.hiring_signal}</strong>
          </Typography>
        )}
      </Sheet>

      <Typography level="title-lg" my={2}>Evaluation Scores</Typography>
      <Stack spacing={2}>
        {[
          {
            title: 'CV Matching',
            data: application.evaluation.cvMatching,
            description: 'How well the candidate\'s CV matches the job requirements'
          },
          {
            title: 'Experience Relevance',
            data: application.evaluation.experienceEvaluation,
            description: 'Relevance and quality of professional experience'
          },
          {
            title: 'Answer Quality & Case Performance',
            data: application.evaluation.answersEvaluation,
            description: 'Performance on interview questions and case studies'
          },
          {
            title: 'Soft Skills',
            data: application.evaluation.softSkillsEvaluation,
            description: 'Communication, leadership, and interpersonal abilities'
          },
          {
            title: 'Role Play',
            data: application.evaluation.rolePlayEvaluation,
            description: 'Performance in simulated role-play scenarios'
          }
        ].map((category, idx) => (
          <Card
            key={idx}
            variant="outlined"
            sx={{
              p: 2,
              borderLeft: 4,
              borderLeftColor: `${getScoreColor(category.data?.score || 0)}.500`,
              transition: 'all 0.2s',
              '&:hover': {
                boxShadow: 'md',
                transform: 'translateY(-2px)'
              }
            }}
          >
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1.5, gap: 2 }}>
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography level="title-md" mb={0.5}>
                  {category.title}
                </Typography>
                <Typography level="body-xs" sx={{ color: 'text.tertiary' }}>
                  {category.description}
                </Typography>
              </Box>
              <Chip
                size="md"
                variant="soft"
                color={getScoreColor(category.data?.score || 0)}
                sx={{
                  minWidth: 60,
                  fontWeight: 600
                }}
              >
                {category.data?.score || 0}/100
              </Chip>
            </Box>
            <Divider sx={{ mb: 1 }} />
            <Typography level="body-sm" sx={{ color: 'text.secondary', lineHeight: 1.6, whiteSpace: 'pre-wrap' }}>
              {category.data?.explanation || 'No details available'}
            </Typography>
          </Card>
        ))}
      </Stack>
    </>
  );
};

const UPDATE_APPLICATION_STAGE = gql`
  mutation UpdateApplicationStage($applicationId: String!, $stage: String!, $message: String) {
    updateApplicationStage(applicationId: $applicationId, stage: $stage, message: $message) {
      _id
      stage
      __typename
    }
  }
`;

export const Candidates = ({ job, showAll, userId, basePath, clientId, user, startDate, endDate }) => {
  const { applicationId } = useParams();
  const navigate = useNavigate();
  const [viewMode, setViewMode] = useState('list'); // 'list' or 'kanban'
  const { pagination, setPageIndex, setPageSize, limit, offset, search, setSearch, clearSearch, searchQuery } = usePagination({ defaultPageSize: 10 });
  const feedback = useFeedback();

  const companyName = user?.client?.company;
  const [updateApplicationStage] = useMutation(UPDATE_APPLICATION_STAGE);
  const [enqueueEvaluateApplication, { loading: isEnqueueEvaluating }] = useMutation(ENQUEUE_EVALUATE_APPLICATION_MUTATION);
  const [transcriptsDialogOpen, setTranscriptsDialogOpen] = useState(false);
  const [transcriptsData, setTranscriptsData] = useState(null);
  const [logsDialogOpen, setLogsDialogOpen] = useState(false);
  const [logsData, setLogsData] = useState(null);
  const { trackOpenCandidatePage } = useTracking();
  const { isTourActive, tourStepIndex, advanceTour, setFirstApplicationData } = useOnboarding();

  const handleOpenDetails = (application) => {
    if (basePath && job) {
      trackOpenCandidatePage(clientId, job._id, application._id, application.user?._id);
      navigate(`${basePath}/${application._id}`);
      if (isTourActive && tourStepIndex === 2) {
        setTimeout(() => advanceTour(), 400);
      }
    }
  };

  const { data: applicationsData, loading: applicationsLoading } = useQuery(GET_APPLICATIONS, {
    variables: {
      jobId: job?._id,
      limit,
      offset,
      startDate: startDate ? startDate : undefined,
      endDate: endDate ? endDate : undefined,
      search: searchQuery,
      showAll: showAll || false
    },
    skip: !job?._id,
    fetchPolicy: 'network-only'
  });

  // Reset page when job, dates change
  useEffect(() => {
    setPageIndex(0);
  }, [job?._id, startDate, endDate, setPageIndex]);

  const [fetchTranscripts, { loading: transcriptsLoading }] = useLazyQuery(GET_APPLICATION, {
    fetchPolicy: 'network-only',
    onCompleted: (data) => {
      setTranscriptsData(data?.application?.interview?.transcripts || []);
      setTranscriptsDialogOpen(true);
    },
    onError: (error) => {
      console.error('Error fetching transcripts:', error);
      feedback.snackbar({ text: 'Failed to load transcripts.', type: 'error' });
    }
  });

  const [fetchInterviewLogs, { loading: logsLoading }] = useLazyQuery(INTERVIEW_LOGS_QUERY, {
    fetchPolicy: 'network-only',
    onCompleted: (data) => {
      setLogsData(data?.interviewLogs?.items || []);
      setLogsDialogOpen(true);
    },
    onError: (error) => {
      console.error('Error fetching interview logs:', error);
      feedback.snackbar({ text: 'Failed to load interview logs.', type: 'error' });
    }
  });

  const { regularApplications, demoApplications, totalCount } = useMemo(() => {
    const items = applicationsData?.applications?.items || [];
    const total = applicationsData?.applications?.totalCount || 0;

    if (items.length === 0) return { regularApplications: [], demoApplications: [], totalCount: total };

    let applications = items.map(x => ({ ...x, evaluation: JSON.parse(x.evaluation || '{}') }));
    applications = applications.map(x => ({ ...x, finalScore: x.score || Math.round(x.evaluation?.finalEvaluation?.score || 0) }));
    applications = applications.map(x => ({ ...x, progress: getApplicationProgress(x) }));

    // Separate applications into regular and demo
    const regularApps = [];
    const demoApps = [];

    applications.forEach(application => {
      const isDemo = userId === application?.user._id;
      if (isDemo) {
        demoApps.push(application);
      } else {
        regularApps.push(application);
      }
    });

    // Sort both arrays
    const sortFunction = showAll
      ? (a, b) => b.progress.step - a.progress.step || new Date(b.updatedAt) - new Date(a.updatedAt)
      : (a, b) => b.finalScore - a.finalScore || new Date(b.updatedAt) - new Date(a.updatedAt);

    regularApps.sort(sortFunction);
    demoApps.sort(sortFunction);

    return { regularApplications: regularApps, demoApplications: demoApps, totalCount: total };
  }, [applicationsData, showAll, userId]);

  const selectedApplication = useMemo(() => {
    if (!applicationId) return null;
    const allApplications = [...regularApplications, ...demoApplications];
    const res = allApplications.find(app => app._id === applicationId) || null;
    console.log("Selected application:", res);
    if (res?.user?.email === "demo@example.com") {
      res.interview = {
        recording: {
          _id: "demo_placeholder",
          state: RECORDING_STATES.READY,
        }
      };
    }
    return res;
  }, [applicationId, regularApplications, demoApplications]);

  const handleCloseDetails = () => {
    if (basePath) {
      navigate(basePath);
    }
  };

  const handleShowScreeningResult = (application) => () => {
    const questions = JSON.parse(application.questionnaire?.questions || "[]") || [];
    const answers = JSON.parse(application.questionnaire?.answers || '{}') || {};
    feedback.alert({
      component: <FormRenderer form={{ questions }} result={answers} isPreview />,
      confirmButtonText: "Close",
      maxWidth: "md",
      fullWidth: true,
      clickAwayClose: true,
      removePadding: true,
    });
  }

  const handleMoveCandidate = async (applicationId, newStage) => {
    let message = null;

    if (newStage === APPLICATION_STAGES.REJECTED) {
      // Find the application to get candidate details
      const allApplications = [...regularApplications, ...demoApplications];
      const application = allApplications.find(app => app._id === applicationId);
      const candidateName = application ? application.user.firstName : 'Candidate';
      const candidateFullName = application ? `${application.user.firstName} ${application.user.lastName}` : 'Candidate';
      const positionTitle = job?.title || 'the position';
      const company = job?.hideClientName ? null : (companyName || 'our company');
      const userName = user ? `${user.firstName} ${user.lastName}` : 'The Hiring Team';

      const defaultMessage = getRejectionMessageTemplate({
        candidateName,
        positionTitle,
        companyName: company,
        userName
      });

      const result = await feedback.confirm({
        title: `Reject ${candidateFullName} for ${positionTitle}`,
        text: (
          <Box sx={{ mb: 2 }}>
            <Typography level="body-md" sx={{ mb: 1.5 }}>
              This message will be sent to the candidate via email.
            </Typography>
            <Typography level="body-sm" color="text.secondary" sx={{ mb: 1 }}>
              The message below is fully editable — feel free to customize the text and add personalized feedback.
            </Typography>
            {/* <Chip size="sm" color="warning" variant="soft">
              Please replace [Tailored Feedback Specific to the Candidate] with actual feedback
            </Chip> */}
          </Box>
        ),
        input: {
          title: 'Rejection Message',
          initialValue: defaultMessage,
          rows: 12,
          required: true
        },
        confirmButtonText: 'Send Rejection',
        maxWidth: 'sm',
        fullWidth: true
      });

      if (!result) {
        return; // User cancelled
      }

      if (result.includes(REJECTION_TEMPLATE_FEEDBACK_PLACEHOLDER)) {
        feedback.snackbar({ text: 'Please replace the feedback placeholder with actual feedback.', type: 'error' });
        return; // Prevent proceeding with the placeholder in the message
      }

      message = result;
    }

    try {
      await updateApplicationStage({
        variables: {
          applicationId,
          stage: newStage,
          message
        },
        optimisticResponse: {
          __typename: 'Mutation',
          updateApplicationStage: {
            _id: applicationId,
            stage: newStage,
            __typename: 'Application'
          }
        }
      });

      feedback.snackbar({ text: "Candidate stage updated successfully.", type: "success" });
    } catch (error) {
      console.error("Error updating application stage:", error);
      feedback.snackbar({ text: parseGraphQLError(error) || "Failed to update candidate stage.", type: "error" });
    }
  }

  const handleManualEvaluate = async (applicationId) => {
    try {
      await enqueueEvaluateApplication({ variables: { applicationId } });
      feedback.snackbar({ text: 'Evaluation has been enqueued.', type: 'success' });
    } catch (error) {
      console.error('Error enqueuing evaluation:', error);
      feedback.snackbar({ text: 'Failed to enqueue evaluation.', type: 'error' });
    }
  };

  const handleViewTranscripts = (applicationId) => {
    if (!applicationId) {
      feedback.snackbar({ text: 'No application found.', type: 'warning' });
      return;
    }
    fetchTranscripts({ variables: { id: applicationId } });
  };

  const handleViewLogs = (interviewId) => {
    if (!interviewId) {
      feedback.snackbar({ text: 'No interview found.', type: 'warning' });
      return;
    }
    fetchInterviewLogs({ variables: { limit: 2000, filters: [{ id: 'interviewId', value: interviewId }] } });
  };

  // Register first regular application for the tour's modal step
  useEffect(() => {
    if (!isTourActive || !regularApplications.length || !job?._id) return;
    setFirstApplicationData(job._id, regularApplications[0]._id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isTourActive, regularApplications[0]?._id, job?._id]);

  const renderApplicationsList = (applications, title, addFirstId = false) => (
    <>
      {title && (
        <Typography level="h3" mb={2}>
          {title}
        </Typography>
      )}
      {applications.length === 0 ? (
        <Card variant="outlined">
          <Typography level="body-md" textAlign="center" py={3}>
            No candidates in this section.
          </Typography>
        </Card>
      ) : (
        <List>
          {applications.map((application, appIndex) => {
            // console.log("Application:", new Date(application.updatedAt), application.finalScore);
            return (
              <Card
                key={application._id}
                variant="outlined"
                id={addFirstId && appIndex === 0 ? 'onboarding-first-candidate' : undefined}
                sx={{ mb: 2 }}
              >
                <Box sx={{
                  display: 'flex',
                  flexDirection: { xs: 'column', sm: 'row' },
                  justifyContent: 'space-between',
                  alignItems: { xs: 'flex-start', sm: 'center' },
                  gap: { xs: 2, sm: 0 }
                }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, minWidth: 0 }}>
                    {application.user.avatarUrl ?
                      <Avatar src={getCdnFileUrl(application.user.avatarUrl)} />
                      :
                      <Avatar>
                        {application.user.firstName[0]}{application.user.lastName[0]}
                      </Avatar>
                    }
                    <Box sx={{ minWidth: 0 }}>
                      <Typography level="title-lg">
                        {application.user.firstName} {application.user.lastName}
                      </Typography>
                      <Typography level="body-sm">
                        {formatTimestamp(application.updatedAt)}
                      </Typography>
                    </Box>
                  </Box>

                  <Box sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                    flexWrap: 'wrap',
                    alignSelf: { xs: 'stretch', sm: 'auto' },
                    justifyContent: { xs: 'flex-start', sm: 'flex-end' }
                  }}>
                    {/* Stage Chip */}
                    <Chip
                      size="sm"
                      color={APPLICATION_STAGES_COLORS[application.stage || APPLICATION_STAGES.APPLICATION_RECEIVED]}
                      variant="soft"
                      sx={{ mb: { xs: 1, sm: 0 } }}
                    >
                      {APPLICATION_STAGES_LABELS[application.stage || APPLICATION_STAGES.APPLICATION_RECEIVED]}
                    </Chip>

                    {showAll && (
                      <Chip
                        size="sm"
                        color={application.progress.color}
                        variant="soft"
                        sx={{ mb: { xs: 1, sm: 0 } }}
                      >
                        {application.progress.label}
                      </Chip>
                    )}

                    {application.evaluation && (
                      <Chip
                        size="md"
                        color={getScoreColor(application.finalScore)}
                        variant="soft"
                        sx={{ mb: { xs: 1, sm: 0 } }}
                      >
                        Score: {application.finalScore}/100
                      </Chip>
                    )}

                    <Button
                      variant="outlined"
                      color="primary"
                      size="sm"
                      onClick={() => handleOpenDetails(application)}
                      startDecorator={<Visibility />}
                      sx={{
                        alignSelf: { xs: 'stretch', sm: 'auto' },
                        mb: { xs: 1, sm: 0 }
                      }}
                    >
                      View Details
                    </Button>
                    {showAll && application.interview?.isFinished && (
                      <Button
                        variant="soft"
                        color="primary"
                        size="sm"
                        onClick={() => handleManualEvaluate(application._id)}
                        loading={isEnqueueEvaluating}
                        sx={{
                          alignSelf: { xs: 'stretch', sm: 'auto' },
                          mb: { xs: 1, sm: 0 }
                        }}
                      >
                        {application.evaluation ? 'Re-evaluate' : 'Evaluate application'}
                      </Button>
                    )}
                  </Box>
                </Box>
              </Card>
            );
          })}
        </List>
      )}
    </>
  );

  const totalApplications = totalCount;

  return (
    <>
      {/* Search and View Mode Toggle */}
      <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, justifyContent: 'space-between', alignItems: { xs: 'stretch', sm: 'center' }, mb: 3, gap: 2 }}>
        <SearchInput
          value={search}
          onChange={setSearch}
          onClear={clearSearch}
        />
        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', justifyContent: { xs: 'space-between', sm: 'flex-end' }, alignItems: 'center' }}>
          <Typography level="body-sm" color="text.secondary">
            {totalCount > 0 ? `${totalCount} candidate${totalCount !== 1 ? 's' : ''}` : '0 candidates'}
          </Typography>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button
              onClick={() => setViewMode('list')}
              variant={viewMode === 'list' ? 'solid' : 'outlined'}
              color="primary"
              size="sm"
              startDecorator={<ViewList />}
              sx={{ minWidth: { xs: 'auto', sm: 'auto' } }}
            >
              List View
            </Button>
            <Button
              onClick={() => setViewMode('kanban')}
              variant={viewMode === 'kanban' ? 'solid' : 'outlined'}
              color="primary"
              size="sm"
              startDecorator={<ViewModule />}
              sx={{ minWidth: { xs: 'auto', sm: 'auto' } }}
            >
              Kanban View
            </Button>
          </Box>
        </Box>
      </Box>

      {applicationsLoading ? (
        <Card variant="outlined">
          <Typography level="body-md" textAlign="center" py={3}>
            Loading candidates...
          </Typography>
        </Card>
      ) : totalApplications === 0 ? (
        <Card variant="outlined">
          <Typography level="body-md" textAlign="center" py={3}>
            {searchQuery ? 'No candidates match your search.' : 'No candidates yet.'}
          </Typography>
        </Card>
      ) : viewMode === 'kanban' ? (
        <Box
          sx={{
            width: { xs: '100vw', sm: 'calc(100vw - var(--Sidebar-width, 240px))' },
            maxWidth: { xs: '100vw', sm: 'calc(100vw - var(--Sidebar-width, 240px))' },
            position: 'relative',
            left: '50%',
            transform: 'translateX(-50%)',
            px: { xs: 2, sm: 3 },
            boxSizing: 'border-box'
          }}
          className="kanban-container"
        >
          <KanbanBoard
            applications={[...regularApplications, ...demoApplications]}
            onViewDetails={handleOpenDetails}
            onMoveCandidate={handleMoveCandidate}
          />
        </Box>
      ) : (
        <>
          {/* Regular Applications Section */}
          {regularApplications.length > 0 && renderApplicationsList(regularApplications, undefined, true)}

          {/* Demo Applications Section */}
          {demoApplications.length > 0 && (
            <>
              {regularApplications.length > 0 && <Divider sx={{ my: 4 }} />}
              {renderApplicationsList(demoApplications, "Demo Applications")}
            </>
          )}

          {/* Pagination Controls */}
          <Pagination
            pagination={pagination}
            onPageChange={setPageIndex}
            onPageSizeChange={setPageSize}
            totalCount={totalCount}
            pageSizeOptions={[5, 10, 25, 50, 100]}
          />
        </>
      )}

      {/* Details Modal */}
      {selectedApplication && (
        <Modal open={Boolean(selectedApplication)} onClose={handleCloseDetails}>
          <ModalDialog
            id="onboarding-candidate-modal"
            sx={{
              maxWidth: { xs: '100vw', sm: 800 },
              width: '100%',
              maxHeight: { xs: '100vh', sm: '90vh' },
              overflowY: 'auto',
              margin: { xs: 0, sm: 'auto' }
            }}
          >
            <ModalClose onClick={handleCloseDetails} />
            {/* Header */}
            <Stack mt={2} spacing={2}>
              {/* User Identity */}
              <Stack
                direction="row"
                alignItems="center"
                spacing={2}
              >
                <Avatar
                  sx={{ width: 56, height: 56, flexShrink: 0 }}
                  src={selectedApplication.user.avatarUrl ? getCdnFileUrl(selectedApplication.user.avatarUrl) : undefined}
                />
                <Stack direction="column" sx={{ minWidth: 0 }}>
                  <Typography level="h4" noWrap>
                    {selectedApplication.user.firstName} {selectedApplication.user.lastName}
                  </Typography>
                  <Typography level="body-xs" color="text.tertiary" noWrap>
                    {selectedApplication.user.email}
                  </Typography>
                </Stack>
              </Stack>

              {/* Actions */}
              <Box
                sx={{
                  display: 'flex',
                  flexWrap: 'wrap',
                  gap: 1,
                  '& > *': {
                    flexShrink: 0
                  }
                }}
              >
                {(selectedApplication.questionnaire.questions || "").length > 4 &&
                  <Button
                    variant="soft"
                    size="sm"
                    endDecorator={<Visibility />}
                    color="neutral"
                    onClick={handleShowScreeningResult(selectedApplication)}
                  >
                    Show Screening Result
                  </Button>
                }
                {selectedApplication.talent.pdfUrl !== "SKIPPED" && (
                  <Button
                    variant="soft"
                    size="sm"
                    component="a"
                    href={getCdnFileUrl(selectedApplication.talent.pdfUrl)}
                    target="_blank"
                    rel="noopener noreferrer"
                    endDecorator={<OpenInNew />}
                    color="neutral"
                  >
                    Open CV
                  </Button>
                )}
                {selectedApplication.talent.linkedin !== "SKIPPED" && (
                  <Button
                    variant="soft"
                    size="sm"
                    component="a"
                    href={socialMediaDictionary.linkedin.baseUrl + selectedApplication.talent.linkedin}
                    target="_blank"
                    rel="noopener noreferrer"
                    endDecorator={<OpenInNew />}
                    color="neutral"
                  >
                    Open LinkedIn
                  </Button>
                )}
                {selectedApplication.interview && (
                  <Button
                    variant="soft"
                    size="sm"
                    color="neutral"
                    onClick={() => handleViewTranscripts(selectedApplication._id)}
                    endDecorator={<Description />}
                    loading={transcriptsLoading}
                  >
                    View Transcripts
                  </Button>
                )}
                {showAll && selectedApplication.interview && (
                  <Button
                    variant="soft"
                    size="sm"
                    color="neutral"
                    onClick={() => handleViewLogs(selectedApplication.interview._id)}
                    endDecorator={<Terminal />}
                    loading={logsLoading}
                  >
                    Show Log
                  </Button>
                )}
              </Box>
              {/* Row 2: stage selector, indented under name/email */}
              <Stack
                direction={{ xs: 'column', sm: 'row' }}
                alignItems={{ xs: 'flex-start', sm: 'center' }}
                spacing={1}
                sx={{ width: '100%' }}
              >
                <Typography level="body-sm" color="text.secondary" sx={{ minWidth: { xs: 'auto', sm: 'fit-content' } }}>
                  Stage:
                </Typography>
                <Select
                  value={selectedApplication.stage || APPLICATION_STAGES.APPLICATION_RECEIVED}
                  onChange={(event, newValue) => {
                    if (newValue && newValue !== selectedApplication.stage) {
                      handleMoveCandidate(selectedApplication._id, newValue);
                    }
                  }}
                  disabled={selectedApplication.stage === APPLICATION_STAGES.REJECTED}
                  sx={{ width: { xs: '100%', sm: 'auto' }, minWidth: { xs: '100%', sm: 200 } }}
                >
                  {Object.values(APPLICATION_STAGES).map((stage) => (
                    <Option key={stage} value={stage}>
                      {APPLICATION_STAGES_LABELS[stage]}
                    </Option>
                  ))}
                </Select>
                {selectedApplication.stage === APPLICATION_STAGES.REJECTED && (
                  <Typography level="body-xs" color="danger">
                    Rejected candidates cannot be moved to another stage
                  </Typography>
                )}
              </Stack>
            </Stack>
            <Divider sx={{ mt: 0 }} />

            {selectedApplication.evaluation ? (
              <EvaluationInfo application={selectedApplication} />
            ) : (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                <Typography level="body-lg">No evaluation data available for this candidate.</Typography>
              </Box>
            )}
          </ModalDialog>
        </Modal>
      )}

      {/* Transcripts Dialog */}
      <Modal open={transcriptsDialogOpen} onClose={() => setTranscriptsDialogOpen(false)}>
        <ModalDialog
          sx={{
            maxWidth: { xs: '100vw', sm: 700 },
            width: '100%',
            maxHeight: { xs: '100vh', sm: '80vh' },
            overflowY: 'auto',
            margin: { xs: 0, sm: 'auto' }
          }}
        >
          <ModalClose onClick={() => setTranscriptsDialogOpen(false)} />
          <Typography level="h4" mb={2}>Interview Transcripts</Typography>
          <Divider />
          <Box sx={{ mt: 2 }}>
            {transcriptsData && transcriptsData.length > 0 ? (
              <Stack spacing={2}>
                {transcriptsData.map((transcript, index) => (
                  <Card key={index} variant="outlined" sx={{ p: 2 }}>
                    <Typography level="title-sm" mb={1}>Section {index + 1}</Typography>
                    <Typography
                      level="body-sm"
                      sx={{
                        whiteSpace: 'pre-wrap',
                        fontFamily: 'monospace',
                        backgroundColor: 'background.level1',
                        p: 1.5,
                        borderRadius: 'sm',
                        maxHeight: 300,
                        overflowY: 'auto'
                      }}
                    >
                      {transcript || 'No transcript available for this section.'}
                    </Typography>
                  </Card>
                ))}
              </Stack>
            ) : (
              <Typography level="body-md" textAlign="center" py={3}>
                No transcripts available for this interview.
              </Typography>
            )}
          </Box>
        </ModalDialog>
      </Modal>

      {/* Interview Logs Dialog */}
      <Modal open={logsDialogOpen} onClose={() => setLogsDialogOpen(false)}>
        <ModalDialog
          sx={{
            maxWidth: { xs: '100vw', sm: 700 },
            width: '100%',
            maxHeight: { xs: '100vh', sm: '80vh' },
            overflowY: 'auto',
            margin: { xs: 0, sm: 'auto' }
          }}
        >
          <ModalClose onClick={() => setLogsDialogOpen(false)} />
          <Typography level="h4" mb={2}>Interview Log</Typography>
          <Divider />
          <Box sx={{ mt: 2 }}>
            {logsData && logsData.length > 0 ? (
              <Stack spacing={1}>
                {[...logsData].sort((a, b) => a.ts - b.ts).map((entry) => (
                  <Card key={entry._id} variant="outlined" sx={{ p: 1.5 }}>
                    <Typography level="body-xs" sx={{ mb: 0.5 }}>
                      <Typography component="span" sx={{ fontWeight: 'bold' }}>{new Date(entry.ts).toLocaleString()}</Typography>
                      {' '}[{entry.tag}] {entry.level}
                    </Typography>
                    <Typography
                      level="body-xs"
                      sx={{
                        whiteSpace: 'pre-wrap',
                        fontFamily: 'monospace',
                        backgroundColor: 'background.level1',
                        p: 1,
                        borderRadius: 'sm',
                        maxHeight: 200,
                        overflowY: 'auto'
                      }}
                    >
                      {(() => {
                        try {
                          return JSON.stringify(JSON.parse(entry.payload || '{}'), null, 2);
                        } catch {
                          return entry.payload || '';
                        }
                      })()}
                    </Typography>
                  </Card>
                ))}
              </Stack>
            ) : (
              <Typography level="body-md" textAlign="center" py={3}>
                No log entries available for this interview.
              </Typography>
            )}
          </Box>
        </ModalDialog>
      </Modal>
    </>
  );
}

const JobCandidates = () => {
  const { jobId } = useParams();
  const { user } = useUser();
  const { trackViewCandidates } = useTracking();

  const job = useMemo(() => user?.client?.jobs.find(job => job._id === jobId), [user, jobId]);

  useEffect(() => {
    window.gtag('event', 'job_candidates_viewed', {});
    if (user?.client?._id && job) {
      trackViewCandidates(user.client._id, job._id, job.title, job.candidatesStats?.total || 0);
    }
  }, [user?.client?._id, job, trackViewCandidates]);

  return (
    <Box sx={{ p: 0 }}>
      <Typography level="h2" mb={3}>
        Candidates for {job?.title}
      </Typography>
      <Candidates job={job} userId={user?._id} basePath={`/job/${jobId}/candidates`} clientId={user?.client?._id} user={user} />
    </Box >
  );
};

export default JobCandidates;